import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messageOf, unwrap } from '@truecare/api-client';
import type { ComplianceArtifactType } from '@truecare/schemas';
import { Alert, Card, CardHeader } from '@truecare/ui';
import { api } from '../api';
import { ArtifactRow, type ArtifactRowData } from './ArtifactRow';
import { ArtifactUploadModal } from './ArtifactUploadModal';

/**
 * Phase 5f-c — caregiver compliance section.
 *
 * Fetches the catalog from /me/caregiver/compliance/catalog and renders
 * one `<ArtifactRow>` per required type. Click handler opens
 * `<ArtifactUploadModal>` which captures the file + dates; the section
 * runs the full upload flow:
 *
 *   1. POST /me/caregiver/compliance/upload-url
 *   2. PUT file directly to S3 (using the signed URL)
 *   3. POST /me/caregiver/compliance with the resulting key + extras
 *   4. Invalidate the catalog + application-readiness queries
 *
 * Per 5f-7 migration (from 15-G): the WSIB_CLEARANCE row exposes the
 * stable WSIB account-number input via `<ArtifactRow>` extras. Pre-fill
 * from the most-recent APPROVED artifact happens automatically inside
 * `<ArtifactRow>` (5f-a-r1 audit closure made this prop-sync correct).
 *
 * Mirrors `<CaregiverCredentialsSection>` shape closely — the only
 * differences are the endpoint paths + type names. If a third
 * catalog-driven section ships (e.g., Volunteer credentials at v2),
 * extracting a shared hook would be principled. At 2 sections the
 * duplication is acceptable.
 */

interface PendingUpload {
  type: ComplianceArtifactType;
  label: string;
  extras: Record<string, string>;
  promptDates: boolean;
}

export function CaregiverComplianceSection({
  readOnly = false,
}: { readOnly?: boolean } = {}) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<PendingUpload | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const catalogQuery = useQuery({
    queryKey: ['caregiver', 'compliance', 'catalog'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/compliance/catalog')),
  });

  const uploadMutation = useMutation({
    mutationFn: async (input: {
      type: ComplianceArtifactType;
      extras: Record<string, string>;
      file: File;
      issuedAt: string | null;
      expiresAt: string | null;
    }) => {
      const urlResp = await unwrap(
        api.POST('/api/v1/me/caregiver/compliance/upload-url', {
          body: {
            type: input.type,
            contentType: input.file.type,
            contentLength: input.file.size,
          },
        }),
      );
      const putResp = await fetch(urlResp.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': input.file.type },
        body: input.file,
      });
      if (!putResp.ok) {
        throw new Error(
          `Could not upload to storage (HTTP ${String(putResp.status)}). Try again.`,
        );
      }
      const externalRef = input.extras['externalReferenceNumber'];
      const recordResp = await unwrap(
        api.POST('/api/v1/me/caregiver/compliance', {
          body: {
            type: input.type,
            objectStorageKey: urlResp.objectStorageKey,
            externalReferenceNumber:
              externalRef === undefined || externalRef === '' ? null : externalRef,
            issuedAt: input.issuedAt,
            expiresAt: input.expiresAt,
          },
        }),
      );
      return recordResp;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caregiver', 'compliance', 'catalog'] });
      qc.invalidateQueries({ queryKey: ['caregiver', 'application-readiness'] });
      setPending(null);
      setUploadError(null);
    },
    onError: (e) => {
      setUploadError(messageOf(e, 'Upload failed. Try again.'));
    },
  });

  if (catalogQuery.isPending) {
    return (
      <Card padding="lg" accent="left" tone="leaf">
        <p className="font-body text-sm text-ink-muted">Loading your compliance documents…</p>
      </Card>
    );
  }

  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <Card padding="lg" accent="left" tone="leaf">
        <Alert tone="danger" title="Couldn't load your compliance documents">
          Please refresh the page.
        </Alert>
      </Card>
    );
  }

  function handleRowAction(item: ArtifactRowData) {
    return ({ extras }: { type: string; extras: Record<string, string> }) => {
      setUploadError(null);
      setPending({
        type: item.type as ComplianceArtifactType,
        label: item.label,
        extras,
        promptDates: true,
      });
    };
  }

  return (
    <>
      <Card padding="lg" accent="left" tone="leaf" data-testid="caregiver-compliance-section">
        <CardHeader
          title="Compliance documents"
          description="TB test is required; general liability insurance is optional. Renew before expiry — we'll email you reminders. (Immunizations are shared separately, and are optional.)"
        />
        <div className="mt-4">
          {catalogQuery.data.items.length === 0 && (
            <p className="font-body text-sm text-ink-muted">
              No compliance documents required at this time.
            </p>
          )}
          {catalogQuery.data.items.map((item: ArtifactRowData) => (
            <ArtifactRow
              key={item.type}
              data={item}
              onUpload={handleRowAction(item)}
              readOnly={readOnly}
            />
          ))}
        </div>
      </Card>

      <ArtifactUploadModal
        open={pending !== null}
        title={pending ? `Upload ${pending.label}` : ''}
        promptDates={pending?.promptDates ?? true}
        referenceDisplay={pending?.extras['externalReferenceNumber'] ?? null}
        error={uploadError}
        onCancel={() => {
          setPending(null);
          setUploadError(null);
        }}
        onSubmit={async ({ file, issuedAt, expiresAt }) => {
          if (pending === null) return;
          await uploadMutation.mutateAsync({
            type: pending.type,
            extras: pending.extras,
            file,
            issuedAt,
            expiresAt,
          });
        }}
      />
    </>
  );
}
